// =============================================================================
// Origin UI — src/pages/leave/LieuClaimsPage.tsx
// Days in Lieu (Oct 2026). Employees claim weekend days / public holidays they
// worked; line manager + HR approve; approved days credit the TOIL balance.
//   - My Claims: status of every claim, detail with approval progress and
//     timeline, resubmit after rejection, cancel while pending/rejected.
//   - All Claims (HR Admin / OQTMan): every claim with filters and totals,
//     for trend visibility. Rows open the approval review page.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { lieuApi, formatLieuDate, formatLieuDays, type LieuClaim } from '@/services/lieuApi';
import { leaveApi } from '@/services/leaveApi';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader, Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import { LieuStatusPill, LieuApprovalProgress, LieuTimeline, LieuClaimFormModal } from './LieuComponents';
import styles from './lieu.module.css';

type Tab = 'mine' | 'all';

export function LieuClaimsPage() {
  const { roles } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canSeeAll = roles.isHRAdmin || roles.isOQTMan;

  const [tab, setTab] = useState<Tab>('mine');
  const [mine, setMine] = useState<LieuClaim[]>([]);
  const [all, setAll] = useState<LieuClaim[] | null>(null);
  const [toilAvailable, setToilAvailable] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [resubmitting, setResubmitting] = useState<LieuClaim | null>(null);
  const [selected, setSelected] = useState<LieuClaim | null>(null);
  const [cancelTarget, setCancelTarget] = useState<LieuClaim | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [acting, setActing] = useState(false);

  // HR filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());

  useEffect(() => { loadMine(); }, []);
  useEffect(() => { if (tab === 'all' && canSeeAll) loadAll(); }, [tab, yearFilter]);

  // Deep link from email: /leave/lieu?claim={id}
  useEffect(() => {
    const id = searchParams.get('claim');
    if (!id) return;
    lieuApi.get(id).then(c => setSelected(c)).catch(() => { /* ignore bad link */ });
  }, [searchParams]);

  async function loadMine() {
    try {
      setLoading(true);
      setError('');
      const [claims, balances] = await Promise.all([
        lieuApi.getMine(),
        leaveApi.getBalance().catch(() => []),
      ]);
      setMine(claims);
      const toil = balances.find(b => b.leaveTypeCode === 'TOIL');
      setToilAvailable(toil ? toil.available : null);
    } catch (err: any) {
      setError(err.message || 'Failed to load your claims');
    } finally {
      setLoading(false);
    }
  }

  async function loadAll() {
    try {
      setError('');
      setAll(await lieuApi.getAll(undefined, yearFilter));
    } catch (err: any) {
      setError(err.message || 'Failed to load claims');
    }
  }

  function upsert(claim: LieuClaim) {
    setMine(prev => {
      const idx = prev.findIndex(c => c.lieuDayClaimId === claim.lieuDayClaimId);
      if (idx === -1) return [claim, ...prev];
      const copy = [...prev];
      copy[idx] = claim;
      return copy;
    });
  }

  async function handleCancel() {
    if (!cancelTarget) return;
    try {
      setActing(true);
      const updated = await lieuApi.cancel(cancelTarget.lieuDayClaimId, cancelReason.trim() || undefined);
      upsert(updated);
      if (selected?.lieuDayClaimId === updated.lieuDayClaimId) setSelected(updated);
      setNotice('Claim cancelled.');
      setCancelTarget(null);
      setCancelReason('');
    } catch (err: any) {
      setError(err.message || 'Could not cancel the claim');
    } finally {
      setActing(false);
    }
  }

  function closeDetail() {
    setSelected(null);
    if (searchParams.get('claim')) {
      searchParams.delete('claim');
      setSearchParams(searchParams, { replace: true });
    }
  }

  const filteredAll = useMemo(() => {
    if (!all) return [];
    return statusFilter === 'all' ? all : all.filter(c => c.status === statusFilter);
  }, [all, statusFilter]);

  const allSummary = useMemo(() => {
    const list = all || [];
    const pending = list.filter(c => c.status === 'Pending').length;
    const approvedDays = list.filter(c => c.status === 'Approved').reduce((s, c) => s + c.days, 0);
    const people = new Set(list.filter(c => c.status === 'Approved').map(c => c.employeeId)).size;
    return { pending, approvedDays, people };
  }, [all]);

  const pendingMine = mine.filter(c => c.status === 'Pending').length;
  const yearOptions = [0, 1, 2].map(n => new Date().getFullYear() - n);

  if (loading) return <LoadingSpinner message="Loading days in lieu..." />;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Days in Lieu"
        subtitle="Claim time back for weekends and public holidays you worked"
        action={
          <button className={styles.primaryBtn} onClick={() => { setResubmitting(null); setShowForm(true); }}>
            + Claim a Day
          </button>
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {notice && <div className={styles.successBanner}>{notice}</div>}

      {/* ─── How it works + TOIL balance ─── */}
      <div className={styles.howItWorks}>
        <div className={styles.step}>
          <span className={styles.stepNum}>1</span>
          <div>
            <div className={styles.stepTitle}>Claim the day</div>
            <div className={styles.stepText}>Pick the weekend day or public holiday you worked, full or half day, and say what you did.</div>
          </div>
        </div>
        <div className={styles.step}>
          <span className={styles.stepNum}>2</span>
          <div>
            <div className={styles.stepTitle}>Line manager & HR approve</div>
            <div className={styles.stepText}>Both get an email and see it in Approvals (web and iPhone). If either rejects, they tell you why.</div>
          </div>
        </div>
        <div className={styles.step}>
          <span className={styles.stepNum}>3</span>
          <div>
            <div className={styles.stepTitle}>Added to your TOIL</div>
            <div className={styles.stepText}>You're notified when approved. Book it under My Leave as TOIL. Up to 5 days carry into next year.</div>
          </div>
        </div>
        <div className={styles.balanceTile}>
          <span className={styles.balanceLabel}>TOIL available</span>
          <span className={styles.balanceValue}>{toilAvailable ?? 0}</span>
          <span className={styles.balanceSub}>days · {new Date().getFullYear()}</span>
        </div>
      </div>

      {/* ─── Tabs ─── */}
      {canSeeAll && (
        <div className={styles.tabs}>
          <button className={`${styles.tab} ${tab === 'mine' ? styles.tabActive : ''}`} onClick={() => setTab('mine')}>
            My Claims ({mine.length})
          </button>
          <button className={`${styles.tab} ${tab === 'all' ? styles.tabActive : ''}`} onClick={() => setTab('all')}>
            All Claims (HR)
          </button>
        </div>
      )}

      {/* ─── My Claims ─── */}
      {tab === 'mine' && (
        <Card padding={false}>
          {mine.length === 0 ? (
            <div className={styles.empty}>
              <p>You haven't claimed any days in lieu yet.</p>
              <button className={styles.primaryBtn} onClick={() => { setResubmitting(null); setShowForm(true); }}>
                Claim a Day
              </button>
            </div>
          ) : (
            <>
              {pendingMine > 0 && (
                <div className={styles.filterBar}>
                  <span className={styles.hint}>{pendingMine} claim{pendingMine === 1 ? '' : 's'} awaiting approval</span>
                </div>
              )}
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Date worked</th>
                    <th>Amount</th>
                    <th>Work done</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th style={{ width: 170 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {mine.map(c => (
                    <tr key={c.lieuDayClaimId} onClick={() => setSelected(c)}>
                      <td className={styles.dateCell}>
                        {formatLieuDate(c.workedDate)}
                      </td>
                      <td>{formatLieuDays(c.days)}</td>
                      <td className={styles.workCell} title={c.reason}>{c.reason}</td>
                      <td><LieuStatusPill claim={c} /></td>
                      <td>{formatLieuDate(c.submittedAt, false)}</td>
                      <td onClick={e => e.stopPropagation()}>
                        {c.canResubmit && (
                          <button className={`${styles.inlineBtn} ${styles.inlineBtnPrimary}`}
                            onClick={() => { setResubmitting(c); setShowForm(true); }}>
                            Update & Resubmit
                          </button>
                        )}
                        {c.canCancel && (
                          <button className={styles.inlineBtn} onClick={() => setCancelTarget(c)}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Card>
      )}

      {/* ─── All Claims (HR) ─── */}
      {tab === 'all' && canSeeAll && (
        <Card padding={false}>
          <div className={styles.filterBar}>
            <select className={styles.select} value={yearFilter} onChange={e => setYearFilter(Number(e.target.value))} aria-label="Year">
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select className={styles.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} aria-label="Status">
              <option value="all">All statuses</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Revoked">Revoked</option>
            </select>
            <div className={styles.summaryChips}>
              <span className={styles.chip}><strong>{allSummary.pending}</strong> pending</span>
              <span className={styles.chip}><strong>{allSummary.approvedDays}</strong> days approved</span>
              <span className={styles.chip}><strong>{allSummary.people}</strong> people</span>
            </div>
          </div>
          {all === null ? (
            <div className={styles.empty}>Loading…</div>
          ) : filteredAll.length === 0 ? (
            <div className={styles.empty}>No claims for this filter.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date worked</th>
                  <th>Amount</th>
                  <th>Work done</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filteredAll.map(c => (
                  <tr key={c.lieuDayClaimId} onClick={() => navigate(`/approvals/lieu/${c.lieuDayClaimId}`)}>
                    <td>
                      <strong>{c.employeeName}</strong>
                      <span className={styles.dateSub}>{c.department}{c.officeLocation ? ` · ${c.officeLocation}` : ''}</span>
                    </td>
                    <td className={styles.dateCell}>{formatLieuDate(c.workedDate)}</td>
                    <td>{formatLieuDays(c.days)}</td>
                    <td className={styles.workCell} title={c.reason}>{c.reason}</td>
                    <td><LieuStatusPill claim={c} /></td>
                    <td>{formatLieuDate(c.submittedAt, false)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* ─── Claim detail ─── */}
      {selected && (
        <div className={styles.overlay} onClick={closeDetail}>
          <div className={styles.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className={styles.modalHeader}>
              <div>
                <h3 className={styles.modalTitle}>Day in Lieu — {formatLieuDate(selected.workedDate)}</h3>
                <p className={styles.modalSub}><LieuStatusPill claim={selected} /></p>
              </div>
              <button className={styles.closeBtn} onClick={closeDetail} aria-label="Close">×</button>
            </div>
            <div className={styles.modalBody}>
              {(selected.status === 'Rejected' || selected.status === 'Revoked') && selected.decisionComments && (
                <div className={styles.rejectionNote}>
                  <strong>{selected.status} by {selected.status === 'Rejected' ? selected.rejectedByName : 'HR'}:</strong> {selected.decisionComments}
                </div>
              )}
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Date worked</span>
                  <span className={styles.detailValue}>{formatLieuDate(selected.workedDate)}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Amount</span>
                  <span className={styles.detailValue}>{formatLieuDays(selected.days)}</span>
                </div>
                <div className={`${styles.detailItem} ${styles.detailItemFull}`}>
                  <span className={styles.detailLabel}>Work done</span>
                  <span className={styles.detailValue}>{selected.reason}</span>
                </div>
              </div>
              <div>
                <h4 className={styles.sectionTitle}>Approvals</h4>
                <LieuApprovalProgress claim={selected} />
              </div>
              <div>
                <h4 className={styles.sectionTitle}>History</h4>
                <LieuTimeline claim={selected} />
              </div>
            </div>
            <div className={styles.modalFooter}>
              {selected.canCancel && (
                <button className={styles.dangerBtn} onClick={() => setCancelTarget(selected)}>Cancel Claim</button>
              )}
              {selected.canResubmit && (
                <button className={styles.primaryBtn} onClick={() => { setResubmitting(selected); setShowForm(true); setSelected(null); }}>
                  Update & Resubmit
                </button>
              )}
              <button className={styles.secondaryBtn} onClick={closeDetail}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Cancel confirm ─── */}
      {cancelTarget && (
        <div className={styles.overlay} onClick={() => setCancelTarget(null)}>
          <div className={styles.modal} style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className={styles.modalHeader}>
              <div>
                <h3 className={styles.modalTitle}>Cancel this claim?</h3>
                <p className={styles.modalSub}>{formatLieuDate(cancelTarget.workedDate)} · {formatLieuDays(cancelTarget.days)}</p>
              </div>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="lieu-cancel">Reason (optional)</label>
                <textarea id="lieu-cancel" className={styles.textarea} rows={2} value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)} />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.secondaryBtn} onClick={() => setCancelTarget(null)} disabled={acting}>Go Back</button>
              <button className={styles.dangerBtn} onClick={handleCancel} disabled={acting}>
                {acting ? 'Cancelling…' : 'Cancel Claim'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── New / resubmit form ─── */}
      {showForm && (
        <LieuClaimFormModal
          existing={resubmitting}
          onClose={() => { setShowForm(false); setResubmitting(null); }}
          onSaved={claim => {
            upsert(claim);
            setShowForm(false);
            setNotice(resubmitting
              ? 'Claim resubmitted. Your line manager and HR have been notified.'
              : 'Claim submitted. Your line manager and HR have been notified.');
            setResubmitting(null);
          }}
        />
      )}
    </div>
  );
}
